import { marked } from 'marked';

// Pure markdown-to-HTML rendering for edit outputs. Extracted from ContentArea.vue
// so it can be unit-tested and reused by the dev preview page.

marked.setOptions({
  gfm: true,
  breaks: true,
});

// ─── Inline share cards ───────────────────────────────────────────────────────

export interface ShareHighlight {
  quote: string;
  type: 'strength' | 'critical' | 'important' | 'strategic';
}

export const TYPE_COLORS: Record<string, string> = {
  strength: '#2d9e5f',
  critical: '#1a5f8a',
  important: '#61aed4',
  strategic: '#7aaec8',
};

const SVG_X = `<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`;
const SVG_THREADS = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94"/></svg>`;
const SVG_FACEBOOK = `<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>`;
const SVG_LINKEDIN = `<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>`;
const SVG_COPY = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
const SVG_CHECK = `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

export { SVG_CHECK, SVG_COPY };

export function parseHighlightsJson(json: string): ShareHighlight[] {
  try {
    const cleaned = json
      .trim()
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/\s*```$/, '');
    const parsed = JSON.parse(cleaned);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (h): h is ShareHighlight =>
        typeof h.quote === 'string' && ['strength', 'critical', 'important', 'strategic'].includes(h.type)
    );
  } catch {
    return [];
  }
}

export function buildShareCardHtml(h: ShareHighlight, index: number): string {
  const color = TYPE_COLORS[h.type] ?? '#61aed4';
  const escaped = h.quote.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<div class="inline-share-card" data-share-card data-share-index="${index}" data-share-quote="${escaped}" style="border-left-color:${color}">
  <div class="inline-share-card__quote">${escaped}</div>
  <div class="inline-share-card__actions">
    <span class="inline-share-label">Share</span>
    <button class="inline-share-btn" data-platform="x" title="Share on X">${SVG_X}</button>
    <button class="inline-share-btn" data-platform="threads" title="Share on Threads">${SVG_THREADS}</button>
    <button class="inline-share-btn" data-platform="facebook" title="Share on Facebook">${SVG_FACEBOOK}</button>
    <button class="inline-share-btn" data-platform="linkedin" title="Share on LinkedIn">${SVG_LINKEDIN}</button>
    <button class="inline-share-btn inline-share-btn--copy" data-platform="copy" title="Copy to clipboard">${SVG_COPY}</button>
  </div>
</div>`;
}

function isBoldHeading(p: string): boolean {
  // Bold-only paragraphs like <p><strong>Title</strong></p> are section headings, not body text
  const inner = p
    .replace(/^<p[^>]*>\s*/, '')
    .replace(/\s*<\/p>$/, '')
    .trim();
  return /^<(?:strong|b)[^>]*>[\s\S]*<\/(?:strong|b)>$/.test(inner);
}

function findMatchingBlockByWords(blocks: string[], text: string): number {
  const words = text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 3);
  if (words.length < 3) return -1;
  const normBlocks = blocks.map(b =>
    b
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .toLowerCase()
  );
  for (let start = 0; start <= words.length - 3; start += 2) {
    const window = words.slice(start, start + 3).join(' ');
    const idx = normBlocks.findIndex(
      (norm, j) => blocks[j] !== '\x00' && !isBoldHeading(blocks[j]!) && norm.includes(window)
    );
    if (idx >= 0) return idx;
  }
  return -1;
}

export function injectShareCards(html: string, highlights: ShareHighlight[]): string {
  const paraRe = /<p(?:\s[^>]*)?>[\s\S]*?<\/p>/g;
  const paras = html.match(paraRe) ?? [];

  let result = html;
  const usedParas = new Set<number>();

  for (let i = 0; i < highlights.length; i++) {
    const h = highlights[i]!;
    const card = buildShareCardHtml(h, i);
    const paraIdx = findMatchingBlockByWords(paras, h.quote);

    if (paraIdx >= 0 && !usedParas.has(paraIdx)) {
      const target = paras[paraIdx]!;
      const pos = result.indexOf(target);
      if (pos !== -1) {
        result = result.slice(0, pos + target.length) + card + result.slice(pos + target.length);
        usedParas.add(paraIdx);
        continue;
      }
    }
    result += card;
  }
  return result;
}

// ─── Markdown rendering ───────────────────────────────────────────────────────

export function parseMarkdown(markdown: string): string {
  if (!markdown) return '';

  // If the content contains section shortcodes, use the formatted letter renderer
  if (markdown.includes('[section:')) {
    return parseFormattedLetter(markdown);
  }

  // Check if the content starts with plain text (not a header)
  const trimmed = markdown.trim();
  const startsWithHeader = /^#{1,6}\s/.test(trimmed);

  // If it doesn't start with a header and starts with text, ensure it gets wrapped
  if (!startsWithHeader && trimmed.length > 0) {
    // Add a newline after the first line if it's not already there
    // This ensures marked properly wraps the first paragraph
    const lines = trimmed.split('\n');
    if (lines.length === 1 || (lines.length > 1 && lines[1]?.trim() !== '')) {
      // Single line or no blank line after first line - add one
      markdown = lines[0] + '\n\n' + lines.slice(1).join('\n');
    }
  }

  return marked.parse(markdown) as string;
}

const CHEVRON_SVG = `<svg class="section-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>`;

const SECTION_LABELS: Record<string, string> = {
  strength: 'Strength',
  critical: 'Critical',
  important: 'Important',
  strategic: 'Strategic',
};

export function renderMarkdownChunk(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  return marked.parse(trimmed) as string;
}

// Remove markdown thematic breaks (---, ***, ___). Models pepper these between
// sections; the card layout provides its own separation.
export function stripThematicBreaks(text: string): string {
  return text.replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '');
}

export function parseFormattedLetter(content: string): string {
  // Optionally capture a bold line (**Title**) or heading (## Title) immediately
  // before the [section:] tag — the formatting agent often leaves these outside the tag.
  const sectionRegex =
    /(?:(?:\*\*([^*\n]+)\*\*|#{1,6} ([^\n]+))\s*\n+)?\[section:(strength|critical|important|strategic)\]([\s\S]*?)\[\/section\]/g;
  const parts: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let prevType: string | null = null;

  while ((match = sectionRegex.exec(content)) !== null) {
    // Prose before this section (the optional title capture means match.index
    // starts at the bold/heading line, so it won't appear in prose twice)
    if (match.index > lastIndex) {
      const prose = renderMarkdownChunk(stripThematicBreaks(content.slice(lastIndex, match.index)));
      if (prose) parts.push(`<div class="letter-prose">${prose}</div>`);
    }

    const externalTitle = match[1] ?? match[2] ?? ''; // bold or heading text before the tag
    const type = match[3] ?? 'important';

    // The one divider worth keeping: the boundary between the critical block
    // and the secondary (important) block that follows it.
    if (prevType === 'critical' && type === 'important') {
      parts.push('<div class="letter-prose"><hr></div>');
    }
    prevType = type;
    const sectionRaw = (match[4] ?? '').trim();
    const parsedSection = renderMarkdownChunk(sectionRaw);

    // Prefer the externally captured title; fall back to first h2/h3 inside the body
    const internalHeadingMatch = parsedSection.match(/<h[23][^>]*>(.*?)<\/h[23]>/);
    const titleHtml = externalTitle || (internalHeadingMatch?.[1] ?? '');
    const bodyHtml = parsedSection.replace(/<h[23][^>]*>.*?<\/h[23]>/, '').trim();

    const label = SECTION_LABELS[type] ?? type;

    parts.push(`
      <div class="editorial-section editorial-section--${type}" data-editorial-section>
        <div class="editorial-section__header" data-editorial-toggle>
          <div class="editorial-section__header-left">
            <span class="editorial-section__badge">${label}</span>
            <span class="editorial-section__title">${titleHtml}</span>
          </div>
          ${CHEVRON_SVG}
        </div>
        <div class="editorial-section__body">${bodyHtml}</div>
      </div>
    `);

    lastIndex = sectionRegex.lastIndex;
  }

  // Remaining prose after last section
  if (lastIndex < content.length) {
    const prose = renderMarkdownChunk(stripThematicBreaks(content.slice(lastIndex)));
    if (prose) parts.push(`<div class="letter-prose">${prose}</div>`);
  }

  // If no sections were found, fall back to plain markdown
  if (parts.length === 0) {
    return renderMarkdownChunk(content);
  }

  return parts.join('\n');
}
